import { useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ArrowRight, BarChart3, FileText, Flag, Lightbulb, Play, Search } from 'lucide-react';
import LandingHeader from '../components/LandingHeader';
import HomeStrategyDemo from '../components/HomeStrategyDemo';
import HomeFeatureDemos from '../components/HomeFeatureDemos';
import FeaturedAdvisers from '../advisers/FeaturedAdvisers';
import { useTrackEvent } from '../components/AnalyticsProvider';
import { CLOUDFLARE_STATIC_ASSETS } from '../config/cloudflareAssets';
import './LandingPage.css';

const steps = [
  {
    title: 'Foundation',
    description: 'Set your vision, mission and objectives.',
    icon: FileText,
    to: '/foundation',
  },
  {
    title: 'External assessment',
    description: 'Explore your market, industry and competitors.',
    icon: Search,
    to: '/external-assessment',
  },
  {
    title: 'Internal assessment',
    description: 'Understand your capabilities, strengths and gaps.',
    icon: BarChart3,
    to: '/internal-assessment',
  },
  {
    title: 'Strategy formulation',
    description: 'Develop and refine your strategic plan.',
    icon: Lightbulb,
    to: '/strategy-formulation',
  },
  {
    title: 'Execution',
    description: 'Turn strategy into projects and measurable progress.',
    icon: Flag,
    to: '/execution-monitoring',
  },
];
const solutionLinks = [
  ['Strategic Planning Software', '/strategic-planning-software'],
  ['Strategy Execution Software', '/strategy-execution-software'],
  ['Business Strategy Software', '/business-strategy-software'],
  ['Strategic Alignment Software', '/strategic-alignment-software'],
  ['OKR Software', '/okr-software'],
  ['Strategy Management Software', '/strategy-management-software'],
  ['Strategic Planning Tools', '/strategic-planning-tools'],
  ['Business Planning Software', '/business-planning-software'],
  ['Strategic Execution Platform', '/strategic-execution-platform'],
  ['Corporate Strategy Software', '/corporate-strategy-software'],
];

export default function LandingPage() {
  const location = useLocation();
  useEffect(() => {
    if (!['#framework', '#features', '#home-demo'].includes(location.hash)) return;
    const frame = requestAnimationFrame(() => {
      document.getElementById(location.hash.slice(1))?.scrollIntoView();
    });
    return () => cancelAnimationFrame(frame);
  }, [location.hash]);
  const trackEvent = useTrackEvent();
  const trackSignup = (label: string) =>
    trackEvent({ action: 'sign_up_click', category: 'landing_page', label });

  return (
    <div className="stratplan-home">
      <a className="home-skip-link" href="#home-main">
        Skip to content
      </a>
      <LandingHeader
        className="home-header"
        showFrameworkLink
        onSignUpClick={() => trackSignup('header_cta')}
        onSignInClick={() =>
          trackEvent({ action: 'sign_in_click', category: 'landing_page', label: 'header_sign_in' })
        }
      />
      <main id="home-main">
        <section className="home-hero" aria-labelledby="home-title">
          <div className="home-container">
            <div className="home-hero-copy">
              <p className="home-eyebrow">AI-powered strategy. Connected execution.</p>
              <h1 id="home-title">
                Your entire strategy.
                <span className="home-gradient-text">Connected from day one.</span>
              </h1>
              <p className="home-hero-description">
                Bring your vision, business analysis, priorities, people and projects together.
                <br className="home-desktop-break" /> Plan with guided frameworks and AI, then put
                your strategy to work.
              </p>
              <div className="home-actions">
                <Link
                  to="/sign-up"
                  className="home-button home-button-primary"
                  onClick={() => trackSignup('hero_cta')}
                >
                  Start my strategic plan <ArrowRight aria-hidden="true" size={19} />
                </Link>
                <a href="#features" className="home-button home-button-secondary">
                  <Play aria-hidden="true" size={18} /> Explore the platform
                </a>
              </div>
            </div>
            <HomeStrategyDemo />
          </div>
        </section>
        <section className="home-context home-container" aria-labelledby="home-context-title">
          <p className="home-eyebrow">Connected intelligence</p>
          <h2 id="home-context-title">Your company context. Every stage of your strategy.</h2>
          <p>
            AI helps you draft direction, develop strategies and break work into practical next
            steps. You stay in control.
          </p>
          <Link to="/ai-insights" className="home-inline-link">
            Explore AI assistance <ArrowRight aria-hidden="true" size={16} />
          </Link>
        </section>
        <section id="features" className="home-features" aria-labelledby="home-features-title">
          <div className="home-container">
            <div className="home-section-heading home-feature-heading">
              <h2 id="home-features-title">
                Everything your strategy needs.
                <span className="home-gradient-text">One connected platform.</span>
              </h2>
              <p>
                From big-picture thinking to day-to-day execution, StratPlan brings it all together
                in one workspace.
              </p>
            </div>
            <HomeFeatureDemos />
          </div>
        </section>
        <section
          id="framework"
          className="home-framework home-container"
          aria-labelledby="home-framework-title"
        >
          <div className="home-section-heading">
            <h2 id="home-framework-title">One guided journey, from vision to action.</h2>
            <p>
              A clear five-step path to build your plan and put it to work, with AI assistance along
              the way.
            </p>
          </div>
          <ol className="home-step-list">
            {steps.map(({ title, description, icon: Icon, to }, index) => (
              <li key={title}>
                <Link to={to} className="home-step">
                  <span className={`home-step-number home-step-number-${index + 1}`}>
                    {index + 1}
                  </span>
                  <Icon className="home-step-icon" size={26} strokeWidth={1.8} aria-hidden="true" />
                  <h3>{title}</h3>
                  <p>{description}</p>
                </Link>
                {index < steps.length - 1 && (
                  <ArrowRight className="home-step-arrow" size={18} aria-hidden="true" />
                )}
              </li>
            ))}
          </ol>
        </section>
        <FeaturedAdvisers />
        <section className="home-final-cta home-container" aria-labelledby="home-cta-title">
          <div>
            <h2 id="home-cta-title">Give your ambition a clear plan.</h2>
            <p>Build your strategy. Connect your team. Know what comes next.</p>
          </div>
          <div className="home-actions">
            <Link
              to="/sign-up"
              className="home-button home-button-primary"
              onClick={() => trackSignup('footer_cta')}
            >
              Create my strategic plan <ArrowRight aria-hidden="true" size={18} />
            </Link>
            <Link
              to="/health-check"
              className="home-inline-link"
              onClick={() =>
                trackEvent({
                  action: 'health_check_click',
                  category: 'landing_page',
                  label: 'footer_health_check',
                })
              }
            >
              Take the strategy health check <ArrowRight aria-hidden="true" size={16} />
            </Link>
          </div>
        </section>
      </main>
      <footer className="home-footer home-container">
        <div className="home-footer-top">
          <Link to="/" aria-label="StratPlan homepage">
            <img
              src={CLOUDFLARE_STATIC_ASSETS.LOGO_LIGHT}
              alt="StratPlan"
              width={168}
              height={64}
            />
          </Link>
          <nav aria-label="Footer navigation">
            <a href="#features">Product</a>
            <a href="#framework">How it works</a>
            <Link to="/health-check">Free tools</Link>
            <Link to="/pricing">Pricing</Link>
            <Link to="/advisers">Advisers</Link>
            <Link to="/about">Company</Link>
            <Link to="/blog">Blog</Link>
          </nav>
          <p>A more connected tomorrow.</p>
        </div>
        <div className="home-footer-bottom">
          <p>© {new Date().getFullYear()} StratPlan. All rights reserved.</p>
          <div>
            <Link to="/privacy">Privacy</Link>
            <Link to="/contact">Contact</Link>
          </div>
        </div>
        <details className="home-solution-links">
          <summary>Explore strategic planning solutions</summary>
          <nav aria-label="Strategic planning solutions">
            {solutionLinks.map(([label, to]) => (
              <Link key={to} to={to}>
                {label}
              </Link>
            ))}
          </nav>
        </details>
      </footer>
    </div>
  );
}
